





import { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ------------------------------------------------------------------ *
 * Config
 * Tip: add the Inter font in index.html for the intended look:
 * <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
 * and set `fontFamily: { sans: ["Inter", ...] }` in tailwind.config.
 * ------------------------------------------------------------------ */
// Paths are relative to REACT_APP_API_URL (same as your other installer calls).
const PAYMENTS_PATH = "/installer/msedcl-selection-pay";
const INSTALLERS_PATH = "/installer/options"; // optional: { data: [{ _id, name }] }, adjust or ignore

if (!process.env.REACT_APP_API_URL) {
  console.error("REACT_APP_API_URL is not set. Requests will hit the React dev server and return 404.");
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  // withCredentials: true, // if you authenticate with cookies
});
api.interceptors.request.use((config) => {
  // const token = localStorage.getItem("token");
  // if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS = {
  pending: { label: "Pending", badge: "bg-amber-50 text-amber-700 ring-amber-200", dot: "bg-amber-500" },
  approved: { label: "Approved", badge: "bg-blue-50 text-blue-700 ring-blue-200", dot: "bg-blue-500" },
  paid: { label: "Paid", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" },
  payment_failed: { label: "Payment failed", badge: "bg-red-50 text-red-700 ring-red-200", dot: "bg-red-500" },
  rejected: { label: "Rejected", badge: "bg-slate-100 text-slate-600 ring-slate-200", dot: "bg-slate-400" },
};
const TABS = [{ value: "", label: "All" }, ...Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))];

const SORTS = [
  { value: "createdAt:desc", label: "Newest requested" },
  { value: "createdAt:asc", label: "Oldest requested" },
  { value: "amount:desc", label: "Amount: high to low" },
  { value: "amount:asc", label: "Amount: low to high" },
  { value: "paymentTransferAt:desc", label: "Recently transferred" },
  { value: "approvedAt:desc", label: "Recently approved" },
];

const EMPTY = {
  search: "", status: "", installerId: "", fromDate: "", toDate: "", minAmount: "", maxAmount: "",
  sort: "createdAt:desc",
};

/* ------------------------------ helpers ------------------------------ */
const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const AVATAR_TONES = [
  "bg-indigo-100 text-indigo-700", "bg-sky-100 text-sky-700", "bg-teal-100 text-teal-700",
  "bg-violet-100 text-violet-700", "bg-rose-100 text-rose-700", "bg-orange-100 text-orange-700",
];
const toneFor = (s = "") => AVATAR_TONES[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
const initials = (n = "?") => n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function pageWindow(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, page - 1, page, page + 1]);
  const list = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  list.forEach((p, i) => {
    if (i && p - list[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
}

function exportCsv(rows) {
  const head = ["Installer", "Contact", "Farmer ID", "Saral ID", "Amount", "Status", "Requested", "Approved", "Transferred"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((p) =>
    [p.installer?.name, p.installer?.contact, p.farmerId, p.saralId, p.amount, STATUS[p.status].label,
     p.createdAt, p.approvedAt, p.paymentTransferAt].map(esc).join(",")
  );
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `installer-payments-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/* ------------------------------- icons ------------------------------- */
const paths = {
  search: "M21 21l-4.3-4.3M17 11a6 6 0 11-12 0 6 6 0 0112 0z",
  filter: "M3 5h18M6 12h12M10 19h4",
  x: "M6 6l12 12M18 6L6 18",
  download: "M12 4v12m0 0l-4-4m4 4l4-4M4 20h16",
  refresh: "M4 4v6h6M20 20v-6h-6M5.6 15A8 8 0 0018.4 9M18.4 9L20 10M5.6 15L4 14",
  chevronR: "M9 6l6 6-6 6",
  chevronL: "M15 6l-6 6 6 6",
  copy: "M9 9h10v10H9zM5 15V5h10",
  check: "M5 12l5 5L20 7",
  inbox: "M4 13l2-8h12l2 8M4 13v6h16v-6M4 13h5a3 3 0 006 0h5",
};
const Icon = ({ name, className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={paths[name]} />
  </svg>
);

/* ---------------------------- small pieces --------------------------- */
const control =
  "h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";
const btn =
  "inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:cursor-not-allowed disabled:opacity-50";

const StatusBadge = ({ status }) => {
  const s = STATUS[status];
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const Avatar = ({ name, size = "h-9 w-9 text-sm" }) => (
  <span className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${size} ${toneFor(name)}`}>
    {initials(name)}
  </span>
);

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function CopyButton({ value }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
      className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      aria-label="Copy"
    >
      <Icon name={done ? "check" : "copy"} className="h-3.5 w-3.5" />
    </button>
  );
}

function SkeletonRows({ n = 6 }) {
  return Array.from({ length: n }).map((_, i) => (
    <tr key={i} className="animate-pulse">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-200" />
          <div className="space-y-2">
            <div className="h-3 w-32 rounded bg-slate-200" />
            <div className="h-2.5 w-24 rounded bg-slate-100" />
          </div>
        </div>
      </td>
      {[28, 16, 20, 20, 20].map((w, j) => (
        <td key={j} className="px-6 py-4"><div className="h-3 rounded bg-slate-200" style={{ width: `${w * 4}px` }} /></td>
      ))}
      <td />
    </tr>
  ));
}

/* ------------------------------ drawer ------------------------------- */
function Step({ title, state, date, by, last }) {
  const color = { done: "bg-emerald-500", fail: "bg-red-500", wait: "bg-slate-300" }[state];
  return (
    <li className="relative pb-6 pl-8 last:pb-0">
      {!last && <span className="absolute left-[7px] top-4 h-full w-px bg-slate-200" />}
      <span className={`absolute left-0 top-1 h-4 w-4 rounded-full ring-4 ring-white ${color}`} />
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="text-xs text-slate-500">{date ? fmtDateTime(date) : "Not yet"}</p>
      {by && <p className="mt-0.5 break-all text-xs text-slate-400">By {by}</p>}
    </li>
  );
}

function Detail({ p, onClose }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const i = p.installer;
  const approvalState = p.approvedStatus === true ? "done" : p.approvedStatus === false ? "fail" : "wait";
  const transferState = p.paymentTransferStatus === true ? "done" : p.paymentTransferStatus === false ? "fail" : "wait";

  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-right text-sm text-slate-900">{children}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <aside role="dialog" aria-modal="true" className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-sm text-slate-500">Payment request</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{inr(p.amount)}</p>
            <div className="mt-2"><StatusBadge status={p.status} /></div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <Icon name="x" className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
          <section>
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Installer</h3>
            {i ? (
              <>
                <div className="flex items-center gap-3">
                  <Avatar name={i.name} size="h-11 w-11 text-base" />
                  <div>
                    <p className="font-medium text-slate-900">{i.name}</p>
                    <p className="text-sm text-slate-500">{i.contact} · {i.district}, {i.state}</p>
                  </div>
                </div>
                <dl className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200 px-4">
                  <Row label="Account holder">{i.bankDetails.accountHolderName}</Row>
                  <Row label="Bank">{i.bankDetails.bankName}</Row>
                  <Row label="Account number"><span className="tabular-nums">{i.bankDetails.accountNumber}</span></Row>
                  <Row label="IFSC">{i.bankDetails.IFSC_code}</Row>
                </dl>
              </>
            ) : (
              <p className="text-sm text-slate-500">Installer record not found ({p.installerId}).</p>
            )}
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-slate-900">References</h3>
            <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 px-4">
              <Row label="Farmer ID"><span className="break-all">{p.farmerId}</span> <CopyButton value={p.farmerId} /></Row>
              <Row label="Saral ID">{p.saralId ? <>{p.saralId} <CopyButton value={p.saralId} /></> : "—"}</Row>
              <Row label="Payment ID"><span className="break-all text-xs">{p.id}</span> <CopyButton value={p.id} /></Row>
            </dl>
          </section>

          <section>
            <h3 className="mb-4 text-sm font-semibold text-slate-900">Timeline</h3>
            <ol>
              <Step title="Requested" state="done" date={p.createdAt} by={p.createdBy} />
              <Step
                title={p.approvedStatus === false ? "Rejected" : "Approved"}
                state={approvalState} date={p.approvedAt} by={p.approvedBy}
              />
              <Step
                title={p.paymentTransferStatus === false ? "Payment failed" : "Payment transferred"}
                state={transferState} date={p.paymentTransferAt} by={p.paymentTransferBy} last
              />
            </ol>
          </section>
        </div>
      </aside>
    </div>
  );
}

/* -------------------------------- page ------------------------------- */
export default function MsedclSelectionPayList() {
  const [filters, setFilters] = useState(EMPTY);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [totalAmount, setTotalAmount] = useState(0);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);

  const dSearch = useDebounced(filters.search);
  const dAmounts = useDebounced(`${filters.minAmount}|${filters.maxAmount}`);

  const update = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const on = (key) => (e) => update({ [key]: e.target.value });

  useEffect(() => {
    api
      .get(INSTALLERS_PATH)
      .then(({ data }) => setInstallers(data.data || []))
      .catch(() => setInstallers([]));
  }, []);

  const installerName = useMemo(() => Object.fromEntries(installers.map((i) => [i._id, i.name])), [installers]);

  useEffect(() => {
    const ctrl = new AbortController();
    const [minAmount, maxAmount] = dAmounts.split("|");
    const [sortBy, sortOrder] = filters.sort.split(":");
    const q = { page, limit, sortBy, sortOrder, search: dSearch, status: filters.status,
      installerId: filters.installerId, fromDate: filters.fromDate, toDate: filters.toDate, minAmount, maxAmount };
    // axios serialises this object into the query string; skip empty values
    const params = Object.fromEntries(Object.entries(q).filter(([, v]) => v !== "" && v != null));

    setLoading(true);
    setError("");
    api
      .get(PAYMENTS_PATH, { params, signal: ctrl.signal })
      .then(({ data: j }) => {
        if (!j.success) throw new Error(j.message || "Could not load payments");
        setRows(j.data);
        setPagination(j.pagination);
        setTotalAmount(j.summary?.totalAmount || 0);
      })
      .catch((e) => {
        if (axios.isCancel(e)) return;
        setError(e.response?.data?.message || e.message || "Could not load payments");
      })
      .finally(() => !ctrl.signal.aborted && setLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, dSearch, dAmounts, filters.status, filters.installerId, filters.fromDate, filters.toDate, filters.sort, reload]);

  // removable chips for advanced filters
  const chips = [
    filters.installerId && { key: "installerId", label: `Installer: ${installerName[filters.installerId] || filters.installerId}` },
    filters.fromDate && { key: "fromDate", label: `From ${fmtDate(filters.fromDate)}` },
    filters.toDate && { key: "toDate", label: `To ${fmtDate(filters.toDate)}` },
    filters.minAmount && { key: "minAmount", label: `Min ${inr(filters.minAmount)}` },
    filters.maxAmount && { key: "maxAmount", label: `Max ${inr(filters.maxAmount)}` },
  ].filter(Boolean);

  const hasAny = chips.length || filters.search || filters.status;
  const from = pagination.total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, pagination.total);
  const avg = pagination.total ? Math.round(totalAmount / pagination.total) : 0;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased">
      <div className="mx-auto max-w-[88rem] px-4 py-8 sm:px-8">
        {/* header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <nav className="text-sm text-slate-500" aria-label="Breadcrumb">
              Payments <span className="mx-1.5 text-slate-300">/</span>
              <span className="text-slate-700">Installer payments</span>
            </nav>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">Installer payments</h1>
            <p className="mt-1 text-sm text-slate-500">Review, track and audit selection payments requested for installers.</p>
          </div>
          <div className="flex gap-2">
            <button className={btn} onClick={() => setReload((n) => n + 1)}>
              <Icon name="refresh" /> Refresh
            </button>
            <button className={btn} onClick={() => exportCsv(rows)} disabled={!rows.length}>
              <Icon name="download" /> Export page
            </button>
          </div>
        </div>

        {/* stats */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Stat label="Total amount" value={inr(totalAmount)} hint="Across all results matching your filters" />
          <Stat label="Payment requests" value={pagination.total.toLocaleString("en-IN")} hint="Matching your filters" />
          <Stat label="Average per request" value={inr(avg)} hint="Total amount ÷ requests" />
        </div>

        {/* table card */}
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* tabs */}
          <div className="overflow-x-auto border-b border-slate-200 px-4" role="tablist">
            <div className="flex min-w-max gap-6">
              {TABS.map((t) => {
                const active = filters.status === t.value;
                return (
                  <button
                    key={t.value} role="tab" aria-selected={active}
                    onClick={() => update({ status: t.value })}
                    className={`-mb-px border-b-2 py-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
                      active ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* toolbar */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="relative min-w-[16rem] flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400"><Icon name="search" /></span>
              <input
                className={`${control} pl-9`} value={filters.search} onChange={on("search")}
                placeholder="Search installer, contact, farmer or saral ID"
              />
            </div>
            <select className={`${control} !w-auto`} value={filters.sort} onChange={on("sort")} aria-label="Sort">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button className={`${btn} ${showFilters ? "!border-indigo-500 !text-indigo-600" : ""}`} onClick={() => setShowFilters((v) => !v)}>
              <Icon name="filter" /> Filters
              {chips.length > 0 && (
                <span className="rounded-full bg-indigo-600 px-1.5 text-xs font-semibold text-white">{chips.length}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="grid gap-4 border-t border-slate-100 bg-slate-50/60 px-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
              <label className="block text-xs font-medium text-slate-600">Installer
                {installers.length ? (
                  <select className={`${control} mt-1`} value={filters.installerId} onChange={on("installerId")}>
                    <option value="">All installers</option>
                    {installers.map((i) => <option key={i._id} value={i._id}>{i.name}</option>)}
                  </select>
                ) : (
                  <input className={`${control} mt-1`} placeholder="Installer ID" value={filters.installerId} onChange={on("installerId")} />
                )}
              </label>
              <label className="block text-xs font-medium text-slate-600">Requested from
                <input type="date" className={`${control} mt-1`} value={filters.fromDate} onChange={on("fromDate")} />
              </label>
              <label className="block text-xs font-medium text-slate-600">Requested to
                <input type="date" className={`${control} mt-1`} value={filters.toDate} onChange={on("toDate")} />
              </label>
              <label className="block text-xs font-medium text-slate-600">Min amount
                <input type="number" min="0" className={`${control} mt-1`} placeholder="₹ 0" value={filters.minAmount} onChange={on("minAmount")} />
              </label>
              <label className="block text-xs font-medium text-slate-600">Max amount
                <input type="number" min="0" className={`${control} mt-1`} placeholder="₹ 1,00,000" value={filters.maxAmount} onChange={on("maxAmount")} />
              </label>
            </div>
          )}

          {(chips.length > 0 || hasAny) && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-2.5">
              {chips.map((c) => (
                <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 py-1 pl-3 pr-1.5 text-xs font-medium text-indigo-700">
                  {c.label}
                  <button onClick={() => update({ [c.key]: "" })} className="rounded-full p-0.5 hover:bg-indigo-100" aria-label={`Remove ${c.label}`}>
                    <Icon name="x" className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button onClick={() => { setFilters(EMPTY); setPage(1); }} className="text-xs font-medium text-slate-500 hover:text-slate-800">
                Clear all
              </button>
            </div>
          )}

          {error && (
            <div role="alert" className="flex items-center justify-between border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <span>{error}. Check your connection and try again.</span>
              <button className="font-medium underline" onClick={() => setReload((n) => n + 1)}>Retry</button>
            </div>
          )}

          {/* table */}
          <div className="overflow-x-auto border-t border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium text-slate-500">
                <tr>
                  <th className="px-6 py-3 font-medium">Installer</th>
                  <th className="px-6 py-3 font-medium">Farmer / Saral ID</th>
                  <th className="px-6 py-3 text-right font-medium">Amount</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Requested</th>
                  <th className="px-6 py-3 font-medium">Last updated</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && !rows.length && <SkeletonRows />}

                {rows.map((p) => {
                  const name = p.installer?.name || installerName[p.installerId] || "Unknown installer";
                  return (
                    <tr
                      key={p.id} onClick={() => setSelected(p)} tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setSelected(p)}
                      className={`cursor-pointer hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none ${loading ? "opacity-60" : ""}`}
                    >
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar name={name} />
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-900">{name}</p>
                            <p className="truncate text-xs text-slate-500">
                              {p.installer ? `${p.installer.contact} · ${p.installer.district}` : p.installerId}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <p className="max-w-[14rem] truncate text-slate-900">{p.farmerId}</p>
                        <p className="text-xs text-slate-500">{p.saralId || "No saral ID"}</p>
                      </td>
                      <td className="px-6 py-3.5 text-right font-medium tabular-nums text-slate-900">{inr(p.amount)}</td>
                      <td className="px-6 py-3.5"><StatusBadge status={p.status} /></td>
                      <td className="whitespace-nowrap px-6 py-3.5 text-slate-600">{fmtDate(p.createdAt)}</td>
                      <td className="whitespace-nowrap px-6 py-3.5 text-slate-600">
                        {fmtDate(p.paymentTransferAt || p.approvedAt || p.createdAt)}
                      </td>
                      <td className="pr-4 text-slate-300"><Icon name="chevronR" /></td>
                    </tr>
                  );
                })}

                {!loading && !rows.length && !error && (
                  <tr>
                    <td colSpan={7} className="px-6 py-20 text-center">
                      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                        <Icon name="inbox" className="h-6 w-6" />
                      </span>
                      <p className="mt-4 font-medium text-slate-900">No payments found</p>
                      <p className="mt-1 text-sm text-slate-500">Try a different search, status or date range.</p>
                      {hasAny && (
                        <button className={`${btn} mt-4`} onClick={() => { setFilters(EMPTY); setPage(1); }}>Clear all filters</button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <div className="flex items-center gap-4">
              <span>Showing <b className="font-medium text-slate-900">{from}–{to}</b> of <b className="font-medium text-slate-900">{pagination.total}</b></span>
              <select
                className="h-8 rounded-lg border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} aria-label="Rows per page"
              >
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>

            <nav className="flex items-center gap-1" aria-label="Pagination">
              <button className={`${btn} !h-8 !px-2`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                <Icon name="chevronL" />
              </button>
              {pageWindow(page, pagination.totalPages || 1).map((n, i) =>
                n === "…" ? (
                  <span key={`e${i}`} className="px-2 text-slate-400">…</span>
                ) : (
                  <button
                    key={n} onClick={() => setPage(n)} aria-current={n === page ? "page" : undefined}
                    className={`h-8 min-w-8 rounded-lg px-2 text-sm font-medium ${
                      n === page ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {n}
                  </button>
                )
              )}
              <button className={`${btn} !h-8 !px-2`} disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <Icon name="chevronR" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      {selected && <Detail p={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}